// ModelBand port — verbatim from immersive_gui/ui/src/components/ModelBand.jsx.
// Card per instrument: a full-width Model Select (no inline "Model" label —
// the page-level BandRow on the left of the row owns the label) plus a
// "Grid 120×80 · Domain ±7σ" summary line. Hybrid model still surfaces a
// Mixing row underneath.
import { FC, useContext } from 'react';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';

import EditableNumber from '../../editable-number';
import { tightSelectSx } from '../exotics-shared';
import { Instrument, MODELS, ModelKind } from '../../../../pages/exotics-pricer/contracts';
import { PricingContext } from '../../../../providers/exotics-pricing-provider';

import './exotics-model-band-styles.scss';

interface Props {
  instrumentId: string;
}

const MODEL_LABEL: Record<ModelKind, string> = {
  bs: 'BS (1F PDE)',
  lv: 'LV (1F PDE)',
  sv: 'SV (2F PDE)',
  hybrid_lv_sv: 'Hybrid LV/SV',
};

// MVP DEFAULT_GRID from ModelBand.jsx. Static for now — Instrument type
// doesn't carry a grid field; editor lives in the MVP modal that we
// haven't ported. Displaying the defaults gives the right visual rhythm.
const DEFAULT_GRID = { x: 120, t: 80, xStd: 7 };

const ExoticsModelBandContent: FC<Props> = ({ instrumentId }) => {
  const { instruments, patchInstrument, loading } = useContext(PricingContext);
  const inst = instruments.find((i) => i.id === instrumentId);
  if (!inst) return null;

  const patch = (p: Partial<Instrument>) => patchInstrument(instrumentId, p);
  void loading;

  const renderValue = (v: unknown) => {
    const m = v as ModelKind;
    if (m === 'hybrid_lv_sv' && inst.model === 'hybrid_lv_sv') {
      return `${MODEL_LABEL[m]} (${(inst.mixing ?? 0).toFixed(2)})`;
    }
    return MODEL_LABEL[m];
  };

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(40, 114, 226, 0.06)',
        border: '1px solid #1f3645',
        borderRadius: '6px',
        p: 1,
      }}
    >
      {/* Hidden label preserved for the Playwright selector
          `[id^="model-label-"]`. */}
      <span id={`model-label-${instrumentId}`} style={{ display: 'none' }} />

      <Select
        size="small"
        fullWidth
        variant="outlined"
        value={inst.model}
        renderValue={renderValue}
        onChange={(e) => patch({ model: e.target.value as ModelKind })}
        sx={tightSelectSx}
        inputProps={{ 'aria-label': `model-${instrumentId}` }}
      >
        {MODELS.map((m) => (
          <MenuItem key={m} value={m} sx={{ fontSize: 12 }}>
            {MODEL_LABEL[m]}
          </MenuItem>
        ))}
      </Select>

      {/* Grid / Domain summary — MVP GridSummary pattern. Static text;
          the editor modal isn't ported. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 0.75, px: 0.5 }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 9.5,
            color: 'text.secondary',
            letterSpacing: '0.02em',
          }}
        >
          Grid {DEFAULT_GRID.x}×{DEFAULT_GRID.t}
          <Box component="span" sx={{ color: 'text.disabled', mx: 0.75 }}>
            ·
          </Box>
          Domain ±{DEFAULT_GRID.xStd}σ
        </Typography>
        <Typography sx={{ fontSize: 8, color: 'text.disabled' }}>edit</Typography>
      </Stack>

      {inst.model === 'hybrid_lv_sv' && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.06em',
              color: 'text.secondary',
              minWidth: 50,
            }}
          >
            Mixing
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EditableNumber
              value={inst.mixing ?? 0.5}
              onCommit={(v) => patch({ mixing: v })}
              format="mixing"
              role="caption"
              min={0}
              max={1}
              step={0.05}
              ariaLabel={`mixing-${instrumentId}`}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default ExoticsModelBandContent;
